import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { Input } from "./Input";
import { Button } from "./Button";
import type { CatalogFilterState, StatusFilter } from "../hooks/useCatalogFilter";
import "./CatalogToolbar.css";

export interface CatalogToolbarProps {
  filter: CatalogFilterState<unknown>;
  searchPlaceholder: string;
  /** Omit entirely to hide the status filter (entities with no status column). */
  statusLabels?: { all: string; active: string; inactive: string };
  /** Right-aligned action slot — Import/Export/Template buttons, or nothing. */
  actions?: ReactNode;
}

const STATUS_ORDER: StatusFilter[] = ["all", "active", "inactive"];

export function CatalogToolbar({ filter, searchPlaceholder, statusLabels, actions }: CatalogToolbarProps) {
  const { t: tCommon } = useTranslation("common");

  return (
    <div className="nx-catalog-toolbar">
      <Input
        type="search"
        aria-label={searchPlaceholder}
        placeholder={searchPlaceholder}
        value={filter.query}
        onChange={(e) => filter.setQuery(e.target.value)}
        className="nx-catalog-toolbar__search"
      />
      {statusLabels ? (
        <div className="nx-catalog-toolbar__status" role="group" aria-label={tCommon("status")}>
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              className={[
                "nx-catalog-toolbar__status-pill",
                filter.statusFilter === s ? "nx-catalog-toolbar__status-pill--active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-pressed={filter.statusFilter === s}
              onClick={() => filter.setStatusFilter(s)}
            >
              {statusLabels[s]}
            </button>
          ))}
        </div>
      ) : null}
      <Button variant="ghost" size="sm" disabled={!filter.isFiltered} onClick={filter.clear}>
        {tCommon("clearFilters")}
      </Button>
      {actions ? <div className="nx-catalog-toolbar__actions">{actions}</div> : null}
    </div>
  );
}
